import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import ClientMeetingsTab from "@/pages/ClientMeetingsTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CalendarBlank, Users, Target, ChartBar, PencilSimple, EnvelopeSimple, UserCircle, Flag, List, CaretUp, CaretDown, Phone, Trash, Archive } from "@phosphor-icons/react";
import ProjectTeamPanel from "@/components/ProjectTeamPanel";
import GanttChart from "@/components/GanttChart";
import KanbanBoard from "@/components/KanbanBoard";
import ChangeRequestPanel from "@/components/ChangeRequestPanel";
import DocumentPanel from "@/components/DocumentPanel";
import { SurveyModal, SurveyResults } from "@/components/SurveyModal";
import DependencyGraph, { DependencyGraphLegend } from "@/components/DependencyGraph";
import TaskDetailModal from "@/components/TaskDetailModal";
import { getTaskActionPermissions } from "@/lib/taskPermissions";

const ragColors = {
  green: { bg: "bg-[#F0FDF4]", text: "text-[#16A34A]", label: "On Track" },
  amber: { bg: "bg-[#FFFBEB]", text: "text-[#D97706]", label: "At Risk" },
  red: { bg: "bg-[#FEF2F2]", text: "text-[#DC2626]", label: "Off Track" },
};

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [surveys, setSurveys] = useState([]);
  const [surveyMilestone, setSurveyMilestone] = useState(null);
  const [showEditProject, setShowEditProject] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [internalUsers, setInternalUsers] = useState([]);

  const loadProject = useCallback(async () => {
    // Run each fetch independently. A 403 on any single call (e.g. `/users`
    // for non-admins on certain tenants) must NOT stop the project payload
    // from rendering — otherwise the page hangs on its loading spinner.
    let didLoadProject = false;
    try {
      const projRes = await api.get(`/projects/${projectId}`);
      setProject(projRes.data);
      didLoadProject = true;
    } catch (err) { console.error("Failed to load project:", err); }
    try {
      const tasksRes = await api.get(`/projects/${projectId}/tasks`);
      setTasks(tasksRes.data);
    } catch { /* tasks unavailable — show empty list */ }
    try {
      const surveysRes = await api.get(`/projects/${projectId}/surveys`);
      setSurveys(surveysRes.data);
    } catch { /* surveys unavailable */ }
    try {
      const usersRes = await api.get(`/users?user_type=internal`);
      setInternalUsers(usersRes.data);
    } catch { /* user list unavailable for non-admins — assignee picker degrades */ }
    setLoading(false);
    return didLoadProject;
  }, [projectId]);

  useEffect(() => { loadProject(); }, [loadProject]);

  const handleTaskUpdate = async () => {
    const res = await api.get(`/projects/${projectId}/tasks`);
    setTasks(res.data);
  };

  if (loading || !project) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" />
      </div>
    );
  }

  const rag = ragColors[project.rag_status] || ragColors.green;
  const canUpdateProject = !!project.user_permissions?.can_update;
  const progress = project.task_count > 0 ? Math.round((project.done_count / project.task_count) * 100) : 0;

  // Calculate days remaining against must_complete_by
  let daysRemaining = null;
  if (project.must_complete_by) {
    const end = new Date(project.must_complete_by);
    const now = new Date();
    daysRemaining = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  }

  const openEditProject = () => {
    setEditForm({
      name: project.name || "",
      description: project.description || "",
      start_date: project.start_date || "",
      end_date: project.end_date || "",
      master_start_date: project.master_start_date || "",
      must_complete_by: project.must_complete_by || "",
      pm_user_id: project.pm_user_id || "",
      pm_name: project.pm_name || "",
      pm_email: project.pm_email || "",
      budget: project.budget || 0,
      rag_status: project.rag_status || "green",
    });
    setShowEditProject(true);
  };

  const handleSaveProject = async () => {
    try {
      await api.put(`/projects/${projectId}`, editForm);
      toast.success("Project updated");
      setShowEditProject(false);
      loadProject();
    } catch (err) {
      toast.error("Failed to update project");
    }
  };

  return (
    <div className="page-enter" data-testid="project-detail-page">
      {/* Header */}
      <div className="border-b border-[#E5E5E5] px-6 sm:px-8 py-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="project-title">
                {project.project_number && <span className="text-[#0055FF] mr-2">{project.project_number}</span>}
                {project.name}
              </h1>
              <Badge className={`${rag.bg} ${rag.text} border-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-none`} data-testid="project-rag-status">
                {rag.label}
              </Badge>
            </div>
            <p className="text-sm text-[#525252] mt-1">{project.tenant_name}</p>
          </div>
          {user?.user_type === "internal" && (
            <Button variant="outline" onClick={openEditProject} className="rounded-none border-[#E5E5E5] h-9 px-4 text-xs font-bold uppercase tracking-wider" data-testid="edit-project-button">
              <PencilSimple size={14} className="mr-1.5" /> Edit Project
            </Button>
          )}
        </div>

        {/* Project Meta Row */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-3 text-sm text-[#0A0A0A]">
          {project.pm_name && (
            <span className="flex items-center gap-1.5" data-testid="project-pm">
              <UserCircle size={16} className="text-[#0055FF]" />
              <span className="text-xs font-bold uppercase tracking-wider text-[#525252] mr-1">Eyvo PM:</span>
              <strong>{project.pm_name}</strong>
              {project.pm_email && (
                <a href={`mailto:${project.pm_email}`} className="text-[#0055FF] hover:underline ml-1 flex items-center gap-1 text-xs font-medium">
                  <EnvelopeSimple size={13} /> {project.pm_email}
                </a>
              )}
            </span>
          )}
          {project.client_pm && (
            <span className="flex items-center gap-1.5" data-testid="client-pm">
              <UserCircle size={16} className="text-[#D97706]" />
              <span className="text-xs font-bold uppercase tracking-wider text-[#525252] mr-1">Client PM:</span>
              <strong>{project.client_pm.name}</strong>
              {project.client_pm.email && (
                <a href={`mailto:${project.client_pm.email}`} className="text-[#0055FF] hover:underline ml-1 flex items-center gap-1 text-xs font-medium">
                  <EnvelopeSimple size={13} /> {project.client_pm.email}
                </a>
              )}
              {project.client_pm.phone && (
                <span className="text-xs text-[#525252] flex items-center gap-1 ml-1 font-medium">
                  <Phone size={13} /> {project.client_pm.phone}
                </span>
              )}
            </span>
          )}
        </div>

        {/* Date Timeline + Progress */}
        {project.must_complete_by && project.master_start_date && (() => {
          const startDate = new Date(project.master_start_date);
          const endDate = new Date(project.must_complete_by);
          const now = new Date();
          const totalDuration = endDate - startDate;
          const elapsed = now - startDate;
          const timePercent = Math.max(0, Math.min(100, totalDuration > 0 ? (elapsed / totalDuration) * 100 : 0));
          const totalDays = Math.ceil(totalDuration / (1000 * 60 * 60 * 24));
          const elapsedDays = Math.ceil(elapsed / (1000 * 60 * 60 * 24));
          const isOverdue = now > endDate;

          const gradientStyle = isOverdue
            ? 'linear-gradient(90deg, #16A34A 0%, #84CC16 20%, #EAB308 40%, #F97316 60%, #DC2626 80%, #7C3AED 100%)'
            : 'linear-gradient(90deg, #16A34A 0%, #84CC16 25%, #EAB308 50%, #F97316 75%, #7C3AED 100%)';

          return (
            <div className="mt-4 space-y-3">
              {/* Timeline bar */}
              <div>
                <div className="flex items-center justify-between text-xs text-[#525252] mb-1.5">
                  <span className="flex items-center gap-1 font-medium"><Flag size={12} className="text-[#16A34A]" />{project.master_start_date}</span>
                  <span className="font-bold text-[#0A0A0A]">
                    {isOverdue
                      ? <span className="text-[#DC2626]">{Math.abs(daysRemaining)}d overdue</span>
                      : `${daysRemaining}d of ${totalDays}d remaining`
                    }
                  </span>
                  <span className="flex items-center gap-1 font-medium"><Flag size={12} className="text-[#DC2626]" />{project.must_complete_by}</span>
                </div>
                <div className="relative w-full h-3 bg-[#F5F5F5] rounded-full overflow-hidden">
                  <div className="absolute h-full rounded-full transition-all" style={{ width: `${timePercent}%`, background: gradientStyle }} />
                  {timePercent > 0 && timePercent < 100 && (
                    <div className="absolute top-[-2px] w-[3px] h-[19px] bg-[#0A0A0A] rounded-full shadow-sm" style={{ left: `${timePercent}%`, transform: 'translateX(-50%)' }} title={`Today: Day ${elapsedDays} of ${totalDays}`} />
                  )}
                </div>
                <div className="flex justify-between text-[10px] text-[#525252] mt-0.5 font-medium">
                  <span>Day 1</span>
                  <span className="font-bold text-[#0A0A0A]">Today (Day {Math.min(elapsedDays, totalDays)})</span>
                  <span>Day {totalDays}</span>
                </div>
              </div>

              {/* Task completion progress */}
              <div>
                <div className="flex items-center justify-between text-xs text-[#525252] mb-1">
                  <span className="font-medium">Task Completion</span>
                  <span className="font-bold text-[#0A0A0A]">{progress}% ({project.done_count || 0}/{project.task_count || 0} tasks)</span>
                </div>
                <div className="w-full h-2 bg-[#F5F5F5]">
                  <div className={`h-full transition-all ${progress >= timePercent ? "bg-[#16A34A]" : progress >= timePercent * 0.7 ? "bg-[#D97706]" : "bg-[#DC2626]"}`} style={{ width: `${progress}%` }} />
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Edit Project Dialog */}
      <Dialog open={showEditProject} onOpenChange={setShowEditProject}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Edit Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Project Name</Label>
              <Input value={editForm.name || ""} onChange={e => setEditForm({...editForm, name: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" data-testid="edit-project-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Description</Label>
              <Textarea value={editForm.description || ""} onChange={e => setEditForm({...editForm, description: e.target.value})} className="rounded-none border-[#E5E5E5] min-h-[60px]" data-testid="edit-project-desc" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#DC2626]">Master Start Date</Label>
                <Input type="date" value={editForm.master_start_date || ""} onChange={e => setEditForm({...editForm, master_start_date: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" data-testid="edit-master-start" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#DC2626]">Must Complete By</Label>
                <Input type="date" value={editForm.must_complete_by || ""} onChange={e => setEditForm({...editForm, must_complete_by: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" data-testid="edit-must-complete" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Eyvo Project Manager</Label>
              <Select value={editForm.pm_user_id || ""} onValueChange={v => {
                const pm = internalUsers.find(u => u.id === v);
                setEditForm({...editForm, pm_user_id: v, pm_name: pm?.name || "", pm_email: pm?.email || ""});
              }}>
                <SelectTrigger className="rounded-none border-[#E5E5E5] h-10" data-testid="edit-pm-select"><SelectValue placeholder="Select PM" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {internalUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">RAG Status</Label>
              <Select value={editForm.rag_status || "green"} onValueChange={v => setEditForm({...editForm, rag_status: v})}>
                <SelectTrigger className="rounded-none border-[#E5E5E5] h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="green">Green - On Track</SelectItem>
                  <SelectItem value="amber">Amber - At Risk</SelectItem>
                  <SelectItem value="red">Red - Off Track</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSaveProject} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-10" data-testid="save-project-button">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="border-b border-[#E5E5E5] px-6 sm:px-8">
          <TabsList className="bg-transparent h-12 p-0 gap-0 rounded-none">
            {[
              { value: "overview", label: "Overview" },
              { value: "kanban", label: "Kanban" },
              { value: "list", label: "List" },
              { value: "gantt", label: "Gantt" },
              { value: "dependencies", label: "Dependencies" },
              { value: "documents", label: "Documents" },
              { value: "changes", label: "Change Requests" },
              { value: "meetings", label: "Meetings" },
              ...(user?.user_type === "internal" ? [{ value: "audit", label: "Audit Trail" }] : []),
            ].map(tab => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0055FF] data-[state=active]:text-[#0055FF] data-[state=active]:shadow-none px-4 text-xs font-bold uppercase tracking-wider h-12"
                data-testid={`tab-${tab.value}`}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="p-6 sm:p-8">
          <TabsContent value="overview" className="mt-0">
            <OverviewTab project={project} tasks={tasks} surveys={surveys} onSurveyClick={setSurveyMilestone} user={user} onRefresh={loadProject} />
          </TabsContent>
          <TabsContent value="kanban" className="mt-0">
            <KanbanBoard
              tasks={tasks}
              projectId={projectId}
              user={user}
              onTaskUpdate={handleTaskUpdate}
              onTaskClick={setSelectedTask}
            />
          </TabsContent>
          <TabsContent value="list" className="mt-0">
            <TaskListView tasks={tasks} projectId={projectId} onTaskClick={setSelectedTask} onTaskUpdate={handleTaskUpdate} user={user} canUpdateProject={canUpdateProject} />
          </TabsContent>
          <TabsContent value="gantt" className="mt-0">
            <GanttChart tasks={tasks} onTaskClick={setSelectedTask} onTaskUpdate={handleTaskUpdate} user={user} />
          </TabsContent>
          <TabsContent value="dependencies" className="mt-0">
            <div className="space-y-4" data-testid="dependencies-tab">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-[#0A0A0A]">Task Dependency Graph</h3>
                  <p className="text-xs text-[#A3A3A3] mt-0.5">Visual map of how tasks connect and cascade. Click any task to view details.</p>
                </div>
                <DependencyGraphLegend />
              </div>
              <DependencyGraph
                tasks={tasks}
                onTaskClick={setSelectedTask}
              />
            </div>
          </TabsContent>
          <TabsContent value="documents" className="mt-0">
            <DocumentPanel projectId={projectId} user={user} />
          </TabsContent>
          <TabsContent value="changes" className="mt-0">
            <ChangeRequestPanel projectId={projectId} user={user} />
          </TabsContent>
          <TabsContent value="meetings" className="mt-0">
            {project?.tenant_id ? (
              <ClientMeetingsTab
                clientId={project.tenant_id}
                userTz={user?.timezone}
                readOnly={user?.user_type === "client"}
              />
            ) : (
              <div className="p-6 text-xs text-[#A3A3A3]">Loading meetings…</div>
            )}
          </TabsContent>
          {user?.user_type === "internal" && (
            <TabsContent value="audit" className="mt-0">
              <ProjectAuditTrail projectId={projectId} />
            </TabsContent>
          )}
        </div>
      </Tabs>

      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          user={user}
          projectId={projectId}
          canUpdateProject={canUpdateProject}
          onClose={() => setSelectedTask(null)}
          onUpdate={() => { handleTaskUpdate(); }}
          allTasks={tasks}
        />
      )}

      {surveyMilestone && (
        <SurveyModal
          milestone={surveyMilestone}
          open={!!surveyMilestone}
          onClose={() => setSurveyMilestone(null)}
          onSubmitted={loadProject}
        />
      )}
    </div>
  );
}

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatLocal, formatLocalDate } from "@/lib/timezone";

const STATUS_TOOLTIPS = {
  todo: "Committed tasks with dates assigned, ready to be picked up next.",
  in_progress: "Tasks actively being worked on by a team member.",
  review: "Completed work awaiting review or sign-off before it can be marked done.",
  done: "Tasks that are fully completed, reviewed, and accepted.",
};

function OverviewTab({ project, tasks, surveys, onSurveyClick, user, onRefresh }) {
  const statusCounts = tasks.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {});

  const statusLabels = {
    todo: { label: "To Do", color: "#0055FF" },
    in_progress: { label: "In Progress", color: "#D97706" },
    review: { label: "Review", color: "#7C3AED" },
    done: { label: "Done", color: "#16A34A" },
  };

  return (
    <div className="space-y-6" data-testid="overview-tab-content">
      {project.description && (
        <Card className="border border-[#E5E5E5] rounded-none p-5">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Description</h3>
          <p className="text-sm text-[#525252] leading-relaxed">{project.description}</p>
        </Card>
      )}

      {/* Task Status Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {Object.entries(statusLabels).map(([key, val]) => (
          <TooltipProvider key={key} delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Card className="border border-[#E5E5E5] rounded-none p-4 cursor-help">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: val.color }} />
                    <span className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3] border-b border-dashed border-[#D4D4D4]">{val.label}</span>
                  </div>
                  <span className="text-2xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {statusCounts[key] || 0}
                  </span>
                </Card>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[220px] text-xs bg-[#0A0A0A] text-white rounded-none px-3 py-2">
                <p>{STATUS_TOOLTIPS[key]}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>

      {/* Milestones */}
      {project.milestones?.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">Milestones</h3>
          <div className="space-y-2">
            {project.milestones.map((m) => (
              <div key={m.id} className="flex items-center gap-3 border border-[#E5E5E5] p-3" data-testid={`milestone-item-${m.id}`}>
                <div className={`w-3 h-3 ${m.status === "completed" ? "bg-[#16A34A]" : "border-2 border-[#E5E5E5]"}`} />
                <span className="flex-1 text-sm font-medium text-[#0A0A0A]">{m.title}</span>
                <span className="text-xs text-[#525252]">{m.due_date}</span>
                <Badge className={`text-[10px] font-bold uppercase rounded-none border-0 px-2 py-0.5 ${
                  m.status === "completed" ? "bg-[#F0FDF4] text-[#16A34A]" : "bg-[#F5F5F5] text-[#525252]"
                }`}>
                  {m.status}
                </Badge>
                {m.status === "completed" && (
                  <button
                    onClick={() => onSurveyClick?.(m)}
                    className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF] hover:underline"
                    data-testid={`survey-trigger-${m.id}`}
                  >
                    Give Feedback
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Client Satisfaction Surveys */}
      {surveys?.length > 0 && <SurveyResults surveys={surveys} />}

      {/* Team Management */}
      <ProjectTeamPanel projectId={project.id} team={project.team || []} permissions={project.user_permissions} onRefresh={onRefresh} userType={user?.user_type} projectPm={{ name: project.pm_name, email: project.pm_email }} />
    </div>
  );
}


const statusColors = {
  done: { bg: "bg-[#F0FDF4]", text: "text-[#16A34A]" },
  in_progress: { bg: "bg-[#EFF6FF]", text: "text-[#2563EB]" },
  review: { bg: "bg-[#FEF3C7]", text: "text-[#D97706]" },
  todo: { bg: "bg-[#F5F5F5]", text: "text-[#525252]" },
};

const priorityColors = {
  high: { bg: "bg-[#FEF2F2]", text: "text-[#DC2626]" },
  medium: { bg: "bg-[#FFFBEB]", text: "text-[#D97706]" },
  low: { bg: "bg-[#F0FDF4]", text: "text-[#16A34A]" },
};

function TaskListView({ tasks, projectId, onTaskClick, onTaskUpdate, user, canUpdateProject }) {
  // Default: most recent task first. Mike Jun-17-2026 — the prior
  // "order ASC" default surfaced the oldest pinned task at the top,
  // which doesn't match how operators think about activity ("what
  // changed lately?"). ISO timestamps sort lexicographically same as
  // chronologically, so a desc sort on `created_at` is correct.
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [optimistic, setOptimistic] = useState({});
  const [archiveFilter, setArchiveFilter] = useState("active");
  const [fetchedTasks, setFetchedTasks] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const isInternal = user?.user_type === "internal";
  const STATUS_CYCLE = ["todo", "in_progress", "review", "done"];
  const PRIORITY_CYCLE = ["high", "medium", "low"];
  const cycle = (arr, v) => arr[(arr.indexOf(v) + 1) % arr.length];

  const sourceTasks = fetchedTasks ?? tasks;
  const displayTasks = sourceTasks.map(t => optimistic[t.id] ? { ...t, ...optimistic[t.id] } : t);

  useEffect(() => {
    setOptimistic(prev => {
      let changed = false;
      const next = { ...prev };
      for (const t of tasks) {
        const o = next[t.id];
        if (!o) continue;
        const stillDiffers = Object.entries(o).some(([k, v]) => t[k] !== v);
        if (!stillDiffers) { delete next[t.id]; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [tasks]);

  useEffect(() => {
    if (archiveFilter === "active") { setFetchedTasks(null); return; }
    let cancelled = false;
    api.get(`/projects/${projectId}/tasks?filter=${archiveFilter}`)
      .then(r => { if (!cancelled) setFetchedTasks(r.data); })
      .catch(() => { if (!cancelled) toast.error("Failed to load tasks"); });
    return () => { cancelled = true; };
  }, [archiveFilter, projectId]);

  const doArchiveTask = async () => {
    if (!confirmArchive) return;
    try {
      await api.put(`/tasks/${confirmArchive.id}/archive`);
      toast.success("Task archived");
      setConfirmArchive(null);
      if (archiveFilter !== "active") {
        const r = await api.get(`/projects/${projectId}/tasks?filter=${archiveFilter}`);
        setFetchedTasks(r.data);
      }
      onTaskUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Archive failed");
    }
  };

  const doDeleteTask = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/tasks/${confirmDelete.id}`);
      toast.success("Task deleted");
      setConfirmDelete(null);
      if (archiveFilter !== "active") {
        const r = await api.get(`/projects/${projectId}/tasks?filter=${archiveFilter}`);
        setFetchedTasks(r.data);
      }
      onTaskUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const cycleField = async (e, task, field, values) => {
    e.stopPropagation();
    const next = cycle(values, task[field] || values[0]);
    setOptimistic(o => ({ ...o, [task.id]: { ...(o[task.id] || {}), [field]: next } }));
    try {
      await api.put(`/tasks/${task.id}`, { [field]: next });
      onTaskUpdate?.();
    } catch (err) {
      setOptimistic(o => { const n = { ...o }; delete n[task.id]; return n; });
      toast.error(`Failed to update ${field}`);
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const SortHeader = ({ field, children, className = "" }) => (
    <button onClick={() => toggleSort(field)} className={`flex items-center gap-1 text-left ${className}`} data-testid={`sort-${field}`}>
      {children}
      {sortField === field && (sortDir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
    </button>
  );

  const filtered = displayTasks
    .filter(t => filterStatus === "all" || t.status === filterStatus)
    .filter(t => !search || t.title.toLowerCase().includes(search.toLowerCase()) || (t.assignee_name || "").toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let va = a[sortField] ?? "", vb = b[sortField] ?? "";
      if (sortField === "duration_days") { va = a.duration_days || 0; vb = b.duration_days || 0; }
      if (sortField === "order") { va = a.order ?? 999; vb = b.order ?? 999; }
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });

  const statusCounts = {};
  displayTasks.forEach(t => { statusCounts[t.status] = (statusCounts[t.status] || 0) + 1; });

  return (
    <div className="space-y-4" data-testid="task-list-view">
      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 max-w-xs">
          <Input placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)} className="rounded-none border-[#E5E5E5] h-9 text-xs pl-3" data-testid="list-search-input" />
        </div>
        {isInternal && (
          <div className="flex border border-[#E5E5E5]" data-testid="archive-filter">
            {[{ v: "active", l: "Active" }, { v: "archived", l: "Archived" }, { v: "all", l: "All" }].map(f => (
              <button key={f.v} onClick={() => setArchiveFilter(f.v)} className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${archiveFilter === f.v ? "bg-[#525252] text-white" : "text-[#525252] hover:bg-[#F5F5F5]"}`} data-testid={`archive-filter-${f.v}`}>{f.l}</button>
            ))}
          </div>
        )}
        <div className="flex border border-[#E5E5E5]">
          {[{ v: "all", l: `All (${sourceTasks.length})`, tip: "Show all tasks regardless of status." }, { v: "todo", l: `To Do (${statusCounts.todo || 0})`, tip: STATUS_TOOLTIPS.todo }, { v: "in_progress", l: `In Progress (${statusCounts.in_progress || 0})`, tip: STATUS_TOOLTIPS.in_progress }, { v: "review", l: `Review (${statusCounts.review || 0})`, tip: STATUS_TOOLTIPS.review }, { v: "done", l: `Done (${statusCounts.done || 0})`, tip: STATUS_TOOLTIPS.done }].map(f => (
            <TooltipProvider key={f.v} delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => setFilterStatus(f.v)} className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${filterStatus === f.v ? "bg-[#0055FF] text-white" : "text-[#525252] hover:bg-[#F5F5F5]"}`} data-testid={`filter-${f.v}`}>{f.l}</button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[220px] text-xs bg-[#0A0A0A] text-white rounded-none px-3 py-2">
                  <p>{f.tip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="bg-[#F5F5F5] border-b border-[#E5E5E5]">
              <th className="px-4 py-2.5 text-left"><SortHeader field="title" className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Task</SortHeader></th>
              <th className="px-3 py-2.5 text-left"><SortHeader field="status" className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Status</SortHeader></th>
              <th className="px-3 py-2.5 text-left"><SortHeader field="priority" className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Priority</SortHeader></th>
              <th className="px-3 py-2.5 text-left"><SortHeader field="assignee_name" className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Assignee</SortHeader></th>
              <th className="px-3 py-2.5 text-left"><SortHeader field="start_date" className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Start</SortHeader></th>
              <th className="px-3 py-2.5 text-left"><SortHeader field="end_date" className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">End</SortHeader></th>
              <th className="px-3 py-2.5 text-left"><SortHeader field="duration_days" className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Duration</SortHeader></th>
              <th className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Dependencies</th>
              {isInternal && <th className="px-3 py-2.5 text-right w-12 text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">&nbsp;</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((task) => {
              const sc = statusColors[task.status] || statusColors.todo;
              const pc = priorityColors[task.priority] || priorityColors.medium;
              return (
                <tr key={task.id} onClick={() => onTaskClick(task)} className={`border-b border-[#F5F5F5] cursor-pointer transition-colors ${task.ball_with === "Client" ? "bg-[#FEE2E2] hover:bg-[#FECACA]" : "hover:bg-[#FAFAFA]"}`} data-testid={`list-task-${task.id}`} data-ball-with={task.ball_with || "Eyvo"}>
                  <td className="px-4 py-3">
                    {task.task_number && <span className="text-[10px] font-bold text-[#0055FF] mr-1.5">{task.task_number}</span>}
                    <span className="text-sm font-medium text-[#0A0A0A]">{task.title}</span>
                    {task.is_archived === 1 && <Badge className="ml-2 bg-[#F5F5F5] text-[#A3A3A3] border-0 text-[9px] font-bold uppercase px-1.5 py-0 rounded-none">Archived</Badge>}
                  </td>
                  <td className="px-3 py-3">
                    <Badge
                      onClick={isInternal ? (e) => cycleField(e, task, "status", STATUS_CYCLE) : undefined}
                      title={isInternal ? "Click to cycle status" : undefined}
                      className={`${sc.bg} ${sc.text} border-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-none ${isInternal ? "cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-[#0055FF]" : ""}`}
                      data-testid={`list-status-${task.id}`}
                    >
                      {task.status?.replace(/_/g, " ")}
                    </Badge>
                  </td>
                  <td className="px-3 py-3">
                    <Badge
                      onClick={isInternal ? (e) => cycleField(e, task, "priority", PRIORITY_CYCLE) : undefined}
                      title={isInternal ? "Click to cycle priority" : undefined}
                      className={`${pc.bg} ${pc.text} border-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-none ${isInternal ? "cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-[#0055FF]" : ""}`}
                      data-testid={`list-priority-${task.id}`}
                    >
                      {task.priority}
                    </Badge>
                  </td>
                  <td className="px-3 py-3">
                    {task.assignee_name ? (
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 bg-[#E5E5E5] flex items-center justify-center text-[9px] font-bold text-[#525252] flex-shrink-0">{task.assignee_name.charAt(0).toUpperCase()}</div>
                        <span className="text-xs text-[#525252]">{task.assignee_name}</span>
                      </div>
                    ) : <span className="text-xs text-[#A3A3A3]">—</span>}
                  </td>
                  <td className="px-3 py-3 text-xs text-[#525252]">{task.start_date || <span className="text-[#A3A3A3]">—</span>}</td>
                  <td className="px-3 py-3 text-xs text-[#525252]">{task.end_date || <span className="text-[#A3A3A3]">—</span>}</td>
                  <td className="px-3 py-3 text-xs text-[#525252]">
                    {task.duration_days ? `${task.duration_days}d` : <span className="text-[#A3A3A3]">—</span>}
                  </td>
                  <td className="px-3 py-3 text-xs text-[#A3A3A3]">{task.dependencies?.length > 0 ? `${task.dependencies.length} dep${task.dependencies.length > 1 ? "s" : ""}` : "—"}</td>
                  {isInternal && (() => {
                    const { canArchive, canDelete } = getTaskActionPermissions(task, user, canUpdateProject);
                    return (
                      <td className="px-3 py-3 text-right w-20">
                        {(canArchive || canDelete) && (
                          <div className="flex items-center justify-end gap-1">
                            {canArchive && !task.is_archived && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setConfirmArchive(task); }}
                                className="text-[#A3A3A3] hover:text-[#525252] transition-colors p-1"
                                title="Archive this task"
                                data-testid={`task-archive-btn-${task.id}`}
                              >
                                <Archive size={14} />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setConfirmDelete(task); }}
                                className="text-[#A3A3A3] hover:text-[#DC2626] transition-colors p-1"
                                title={user?.role === "platform_admin" && task.created_by !== user?.id
                                  ? "Delete (platform-admin override)"
                                  : "Delete this task"}
                                data-testid={`task-delete-btn-${task.id}`}
                              >
                                <Trash size={14} />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })()}
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-[#A3A3A3]"><List size={32} className="mx-auto mb-2" /><p className="text-sm font-medium">No tasks match your filters</p></div>
        )}
      </div>
      <p className="text-[10px] text-[#A3A3A3]">Showing {filtered.length} of {sourceTasks.length} tasks &middot; Click column headers to sort &middot; Click a row to view details</p>

      <Dialog open={!!confirmArchive} onOpenChange={() => setConfirmArchive(null)}>
        <DialogContent className="rounded-none max-w-sm">
          <DialogHeader><DialogTitle className="text-sm font-bold uppercase tracking-wider">Archive Task</DialogTitle></DialogHeader>
          <p className="text-sm text-[#525252]">Archive <span className="font-semibold">"{confirmArchive?.title}"</span>? It will be hidden from active views. No data is lost.</p>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" className="flex-1 rounded-none" onClick={() => setConfirmArchive(null)}>Cancel</Button>
            <Button className="flex-1 rounded-none bg-[#525252] hover:bg-[#0A0A0A] text-white font-bold" onClick={doArchiveTask} data-testid="confirm-archive-task">Archive</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="rounded-none max-w-sm">
          <DialogHeader><DialogTitle className="text-sm font-bold uppercase tracking-wider">Delete Task</DialogTitle></DialogHeader>
          <p className="text-sm text-[#525252]">Permanently delete <span className="font-semibold">"{confirmDelete?.title}"</span>? This removes the task and its dependency edges. This cannot be undone.</p>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" className="flex-1 rounded-none" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button className="flex-1 rounded-none bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold" onClick={doDeleteTask} data-testid="confirm-delete-task">Delete Forever</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


const ACTION_LABELS = {
  client_login: { label: "Client Login", color: "text-[#2563EB]", bg: "bg-[#EFF6FF]" },
  cr_created: { label: "Change Request Submitted", color: "text-[#7C3AED]", bg: "bg-[#F5F3FF]" },
  cr_updated: { label: "Request Updated", color: "text-[#D97706]", bg: "bg-[#FFFBEB]" },
  cr_estimate_accepted: { label: "Estimate Accepted", color: "text-[#16A34A]", bg: "bg-[#F0FDF4]" },
  cr_estimate_rejected: { label: "Estimate Rejected", color: "text-[#DC2626]", bg: "bg-[#FEF2F2]" },
  task_created: { label: "Task Created", color: "text-[#0891B2]", bg: "bg-[#ECFEFF]" },
  task_updated: { label: "Task Updated", color: "text-[#525252]", bg: "bg-[#F5F5F5]" },
  task_deleted: { label: "Task Deleted", color: "text-[#DC2626]", bg: "bg-[#FEF2F2]" },
  task_archived: { label: "Task Archived", color: "text-[#A3A3A3]", bg: "bg-[#F5F5F5]" },
  comment_added: { label: "Comment Added", color: "text-[#0055FF]", bg: "bg-[#F0F7FF]" },
  project_updated: { label: "Project Updated", color: "text-[#525252]", bg: "bg-[#F5F5F5]" },
  project_archived: { label: "Project Archived", color: "text-[#A3A3A3]", bg: "bg-[#F5F5F5]" },
  schedule_cascaded: { label: "Schedule Cascaded", color: "text-[#D97706]", bg: "bg-[#FFFBEB]" },
  document_uploaded: { label: "Document Uploaded", color: "text-[#0891B2]", bg: "bg-[#ECFEFF]" },
};

function ProjectAuditTrail({ projectId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/audit-logs/project/${projectId}`);
        setLogs(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [projectId]);

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>;

  return (
    <div data-testid="project-audit-trail">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Audit Trail ({logs.length} events)</h3>
        <Badge className="bg-[#FEF2F2] text-[#DC2626] border-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-none">Internal Only</Badge>
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-12 text-[#A3A3A3]"><p className="font-medium">No audit events for this project yet</p></div>
      ) : (
        <div className="border border-[#E5E5E5]">
          {logs.map((log, i) => {
            const action = ACTION_LABELS[log.action] || { label: log.action?.replace(/_/g, " "), color: "text-[#525252]", bg: "bg-[#F5F5F5]" };
            const time = log.timestamp ? formatLocal(log.timestamp) : "";
            return (
              <div key={log.id || i} className="flex items-start gap-4 px-4 py-3 border-b border-[#F5F5F5] last:border-0 hover:bg-[#FAFAFA]" data-testid={`audit-entry-${log.id}`}>
                <div className="flex-shrink-0 mt-0.5">
                  <Badge className={`${action.bg} ${action.color} border-0 text-[9px] font-bold uppercase px-2 py-0.5 rounded-none whitespace-nowrap`}>{action.label}</Badge>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#0A0A0A]">{log.details || action.label}</p>
                  <p className="text-[10px] text-[#A3A3A3] mt-0.5">
                    {log.user_name || "System"} &middot; {time}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
